import { Archive, Delete } from 'ui/icons';
import { pxToRem } from 'utils/convert';
import { CustomBulkDeletionMixedActionWarning } from './components/CustomBulkDeletionMixedActionWarning';

export enum QUERY_PARAMS {
  PAGE = 'page',
  PAGE_SIZE = 'pageSize',
  SEARCH = 'search',
  STATUS = 'status',
  SORT_BY = 'sortBy',
  SORT_DIRECTION = 'sortDirection',
  FOLDER_ID = 'folderId',
  TRIGGER_TYPE = 'triggerType',
  MOCK_DATA = 'mockData',
  CREATED_BY = 'createdBy',
  UPDATED_BY = 'updatedBy',
  WORKFLOW_TYPE = 'workflowType',
  LABELS = 'labels',
  JOURNEY_VISITOR_TRACKING = 'journeyVisitorTracking',
}

export enum TABLE_COLUMNS {
  NAME = 'Name',
  STATUS = 'State',
  ENTRY_SOURCE = 'Entry source',
  LAST_MODIFIED = 'UpdatedAt',
  SEND_ACTIVITY = 'SendActivity',
}

export enum WORKFLOW_TRIGGER_EVENTS {
  RECEIVED_API_TRIGGER = 'ReceivedApiTrigger',
  TRIGGERED_BY_WORKFLOW = 'TriggeredByWorkflow',
  ADDED_ITEM_TO_CART = 'AddedItemToCart',
  PURCHASED = 'Purchased',
  SCHEDULED_RECURRING_LIST = 'ScheduledRecurringList',
  SUBSCRIBED_TO_CHANNEL = 'SubscribedToChannel',
  SUBSCRIBED_TO_LIST = 'SubscribedToList',
  SUBSCRIBED_TO_MESSAGE_TYPE = 'SubscribedToMessageType',
  UNSUBSCRIBED_FROM_LIST = 'UnsubscribedFromList',
  UNSUBSCRIBED_FROM_MESSAGE_TYPE = 'UnsubscribedFromMessageType',
  UNSUBSCRIBED_FROM_CHANNEL = 'UnsubscribedFromChannel',
  FIELD_CHANGED = 'FieldChanged',
  NEW_USER_CREATED = 'NewUserCreated',
  TRIGGERED_CUSTOM_EVENT = 'TriggeredCustomEvent',
  OPENED_AN_EMAIL = 'OpenedAnEmail',
  CLICKED_AN_EMAIL = 'ClickedAnEmail',
  SEND_AN_EMAIL = 'SentAnEmail',
  CLICKED_AN_SMS = 'ClickedAnSMS',
  RECEIVED_AN_SMS = 'ReceivedAnSMS',
  OPENED_PUSH = 'OpenedPush',
  CLICKED_AN_IN_APP = 'ClickedAnInApp',
  OPENED_AN_IN_APP = 'OpenedAnInApp',
  CLICKED_A_WHATSAPP = 'ClickedWhatsApp',
  RECEIVED_A_WHATSAPP = 'ReceivedWhatsApp',
}

export enum WORKFLOW_CAMPAIGN_DATA_SCIENCE_OPTIMIZATIONS {
  CHANNEL_OPTIMIZATION = 'ChannelOptimization',
  FREQUENCY_OPTIMIZATION = 'FrequencyOptimization',
  SEND_TIME_OPTIMIZATION = 'SendTimeOptimization',
}

export enum WORKFLOW_CAMPAIGN_MESSAGE_MEDIUMS {
  EMAIL = 'Email',
  IN_APP = 'InApp',
  PUSH = 'Push',
  SMS = 'SMS',
  WEB_PUSH = 'WebPush',
}

export const WORKFLOW_TRIGGERS_DISPLAY_NAMES = {
  [WORKFLOW_TRIGGER_EVENTS.RECEIVED_API_TRIGGER]: 'API call',
  [WORKFLOW_TRIGGER_EVENTS.TRIGGERED_BY_WORKFLOW]: 'Other journey',
  [WORKFLOW_TRIGGER_EVENTS.SCHEDULED_RECURRING_LIST]: 'Schedule',
};

// TODO: [WF-5760] Consolidate with TRIGGER_METADATA so that we don't have to change this
// in two places
export const WORKFLOW_TRIGGER_EVENT_DISPLAY_NAMES = {
  ...WORKFLOW_TRIGGERS_DISPLAY_NAMES,
  [WORKFLOW_TRIGGER_EVENTS.TRIGGERED_CUSTOM_EVENT]: 'Custom Event',
  [WORKFLOW_TRIGGER_EVENTS.NEW_USER_CREATED]: 'New user created',
  [WORKFLOW_TRIGGER_EVENTS.ADDED_ITEM_TO_CART]: 'Shopping cart update',
  [WORKFLOW_TRIGGER_EVENTS.PURCHASED]: 'Purchase',
  [WORKFLOW_TRIGGER_EVENTS.FIELD_CHANGED]: 'User profile update',
  [WORKFLOW_TRIGGER_EVENTS.SUBSCRIBED_TO_CHANNEL]: 'Channel subscribe',
  [WORKFLOW_TRIGGER_EVENTS.UNSUBSCRIBED_FROM_CHANNEL]: 'Channel unsubscribe',
  [WORKFLOW_TRIGGER_EVENTS.SUBSCRIBED_TO_LIST]: 'Subscribe',
  [WORKFLOW_TRIGGER_EVENTS.UNSUBSCRIBED_FROM_LIST]: 'Unsubscribe',
  [WORKFLOW_TRIGGER_EVENTS.SUBSCRIBED_TO_MESSAGE_TYPE]: 'Message type subscribe',
  [WORKFLOW_TRIGGER_EVENTS.UNSUBSCRIBED_FROM_MESSAGE_TYPE]: 'Message type unsubscribe',
  [WORKFLOW_TRIGGER_EVENTS.CLICKED_AN_EMAIL]: 'Email Click',
  [WORKFLOW_TRIGGER_EVENTS.OPENED_AN_EMAIL]: 'Email Open',
  [WORKFLOW_TRIGGER_EVENTS.SEND_AN_EMAIL]: 'Email Send',
  [WORKFLOW_TRIGGER_EVENTS.CLICKED_AN_IN_APP]: 'In-App Click',
  [WORKFLOW_TRIGGER_EVENTS.OPENED_AN_IN_APP]: 'In-App Open',
  [WORKFLOW_TRIGGER_EVENTS.OPENED_PUSH]: 'Push Open',
  [WORKFLOW_TRIGGER_EVENTS.CLICKED_AN_SMS]: 'SMS Click',
  [WORKFLOW_TRIGGER_EVENTS.RECEIVED_AN_SMS]: 'SMS Reply',
  [WORKFLOW_TRIGGER_EVENTS.CLICKED_A_WHATSAPP]: 'WhatsApp Click',
  [WORKFLOW_TRIGGER_EVENTS.RECEIVED_A_WHATSAPP]: 'WhatsApp Reply',
};

export const SEARCH_DEBOUNCE_TIMEOUT_MS = 500;

export enum WORKFLOW_STATUS_FILTER {
  ALL = '',
  ENABLED = 'Enabled',
  DISABLED = 'Disabled',
  ARCHIVED = 'Archived',
}

export enum WORKFLOW_STATUS_FILTER_WITH_DRAFTS {
  ALL = '',
  DRAFT = 'Draft',
  ENABLED = 'Enabled',
  DISABLED = 'Disabled',
  ARCHIVED = 'Archived',
  HAS_DRAFT = 'HasDraft',
}

export const WORKFLOW_STATUS_FILTER_WITH_DRAFTS_OPTIONS = [
  {
    id: WORKFLOW_STATUS_FILTER_WITH_DRAFTS.ALL,
    label: 'All',
  },
  {
    id: WORKFLOW_STATUS_FILTER_WITH_DRAFTS.DRAFT,
    label: 'Drafts',
  },
  {
    id: WORKFLOW_STATUS_FILTER_WITH_DRAFTS.ENABLED,
    label: 'Live',
  },
  {
    id: WORKFLOW_STATUS_FILTER_WITH_DRAFTS.DISABLED,
    label: 'Off',
  },
  {
    id: WORKFLOW_STATUS_FILTER_WITH_DRAFTS.ARCHIVED,
    label: 'Archived',
  },
  {
    id: WORKFLOW_STATUS_FILTER_WITH_DRAFTS.HAS_DRAFT,
    label: 'Has draft',
  },
];

/**
 * These are filters that we don't show as filter tabs
 */
export const DISABLED_WORKFLOW_STATUS_FILTER_TABS = [WORKFLOW_STATUS_FILTER_WITH_DRAFTS.HAS_DRAFT];

export const FILTER_MENU_ID_PREFIXES = {
  STATUS: 'status-',
  WORKFLOW_TYPE: 'workflow-type-',
  TRIGGER_TYPE: 'trigger-type-',
  CREATOR: 'creator-',
  MODIFIER: 'modifier-',
  LABEL: 'label-',
  JOURNEY_VISITOR_TRACKING: 'journey-visitor-tracking-',
};

/**
 * These are filters that are labeled differently under status filters
 */
export const OVERWRITTEN_WORKFLOW_STATUS_FILTER_WITH_DRAFTS_OPTIONS = [
  {
    id: WORKFLOW_STATUS_FILTER_WITH_DRAFTS.DRAFT,
    prefix: FILTER_MENU_ID_PREFIXES.WORKFLOW_TYPE,
    label: 'Draft',
  },
  {
    id: WORKFLOW_STATUS_FILTER_WITH_DRAFTS.ENABLED,
    label: 'On',
  },
];

export const TABLE_GRID = `
  ${pxToRem(42)}
  minmax(${pxToRem(240)}, 3fr)
  ${pxToRem(90)}
  minmax(${pxToRem(140)}, 1fr)
  minmax(${pxToRem(140)}, 1fr)
  minmax(${pxToRem(160)}, 1.25fr)
  ${pxToRem(36)}
`;

export const ARCHIVE_MODAL_DISPLAY_TEXT = {
  actionName: {
    verb: 'archive',
    adjective: 'archived',
  },
  itemTypeName: {
    singular: 'journey',
    plural: 'journeys',
  },
  actionIcon: Archive,
};

export const MIXED_MODAL_DISPLAY_TEXT = {
  ...ARCHIVE_MODAL_DISPLAY_TEXT,
  customMixedWarning: CustomBulkDeletionMixedActionWarning,
};

export const DELETE_MODAL_DISPLAY_TEXT = {
  actionName: {
    verb: 'delete',
    adjective: 'deleted',
  },
  itemTypeName: {
    singular: 'journey',
    plural: 'journeys',
  },
  suffixText:
    'This will archive any published journeys in the folder and delete any journey drafts.',
  actionIcon: Delete,
};

export const UNARCHIVE_MODAL_DISPLAY_TEXT = {
  itemTypeName: {
    singular: 'journey',
    plural: 'journeys',
  },
};

export const WORKFLOW_DESCRIPTION_MAX_CHAR_COUNT = 140;

export const WORKFLOW_CSV_HEADER_ROW = [
  'Journey ID',
  'Journey name',
  'Created',
  'Modified',
  'Steps',
  'Started',
  'In Progress',
  'Finished',
  'Status',
  'Created by',
  'Archived',
];
export const WORKFLOW_CSV_DATA_DATE_FORMAT = 'MM-DD-YYYY hh:mma';
export const WORKFLOW_CSV_FILENAME_DATE_FORMAT = 'MM_DD_YYYY_hh_mmA';
export const WORKFLOW_CSV_FILENAME_PREFIX = 'iterable_workflows_';

export enum TOGGLE_MODAL_TYPE {
  ENABLE = 'enable',
  DISABLE = 'disable',
}
export const TOGGLE_MODAL_DISPLAY_TEXT = {
  verb: {
    [TOGGLE_MODAL_TYPE.ENABLE]: 'Turn on',
    [TOGGLE_MODAL_TYPE.DISABLE]: 'Turn off',
  },
  noun: {
    singular: 'journey',
    plural: 'journeys',
  },
  cancelButton: 'Cancel',
};

export enum REDUX_ACTION_TYPES {
  THUMBNAILS_FETCH_REQUESTED = 'WorkflowIndex/THUMBNAILS_FETCH_REQUESTED',
  THUMBNAILS_FETCH_SUCCEEDED = 'WorkflowIndex/THUMBNAILS_FETCH_SUCCEEDED',
  THUMBNAILS_FETCH_FAILED = 'WorkflowIndex/THUMBNAILS_FETCH_FAILED',
  SEND_ACTIVITY_FETCH_REQUESTED = 'WorkflowIndex/SEND_ACTIVITY_FETCH_REQUESTED',
  SEND_ACTIVITY_FETCH_SUCCEEDED = 'WorkflowIndex/SEND_ACTIVITY_FETCH_SUCCEEDED',
  SEND_ACTIVITY_FETCH_FAILED = 'WorkflowIndex/SEND_ACTIVITY_FETCH_FAILED',
  TOGGLE_SELECTION_FOR_ITEM = 'WorkflowIndex/TOGGLE_SELECTION_FOR_ITEM',
  SELECT_ITEMS = 'WorkflowIndex/SELECT_ITEMS',
  DESELECT_ALL_ITEMS = 'WorkflowIndex/DESELECT_ALL_ITEMS',
}

export const COLLAPSED_CAMPAIGN_LIST_MAX_LENGTH = 5;

export const DEFAULT_SKELETON_ROW_COUNT = 5;

export const SKELETON_CAMPAIGN_COUNT = 2;

export const WORKFLOWS_KNOWLEDGE_BASE_URL =
  'https://support.iterable.com/hc/en-us/articles/205480265-Workflows-Overview-';

export const DEFAULT_WORKFLOW_CREATORS = [];
export const DEFAULT_WORKFLOW_MODIFIERS = [];

export const GENERAL_ERROR_MESSAGE = 'Something went wrong. Could not fetch journeys';

export enum SELECTABLE_ITEM_TYPE {
  WORKFLOW_FOLDER = 'WORKFLOW_FOLDER',
  WORKFLOW = 'WORKFLOW',
  WORKFLOW_DRAFT = 'WORKFLOW_DRAFT',
}

export const WORKFLOW_CONTENT_ITEM_NAME = 'journey';

export enum GRAPHQL_CONTENT_FILTER {
  CONTENT_ITEM = 'ContentItem',
  SUBFOLDER = 'Subfolder',
}

export const THUMBNAIL_REQUEST_PAGE_SIZE = 10;

export const ROOT_WORKFLOW_FOLDER_NAME = '__root__';
export const ROOT_WORKFLOW_FOLDER_DISPLAY_NAME = 'Main';

/**
 * These event array constants are used for:
 * 1. Setting `__typename` on the `WorkflowStartNodeData` type
 *    - Keep `as const` at the end of these arrays to maintain typing
 * 2. Configuring the `getWorkflowTriggerResourceName` util function
 * 3. Generating the GraphQL query for the workflow index page
 */
export const WORKFLOW_EVENTS_TRIGGERED_BY_CUSTOM_EVENT = [
  WORKFLOW_TRIGGER_EVENTS.TRIGGERED_CUSTOM_EVENT,
] as const;
export const WORKFLOW_EVENTS_TRIGGERED_BY_LIST = [
  WORKFLOW_TRIGGER_EVENTS.SCHEDULED_RECURRING_LIST,
  WORKFLOW_TRIGGER_EVENTS.SUBSCRIBED_TO_LIST,
  WORKFLOW_TRIGGER_EVENTS.UNSUBSCRIBED_FROM_LIST,
] as const;
export const WORKFLOW_EVENTS_TRIGGERED_BY_CAMPAIGN = [
  WORKFLOW_TRIGGER_EVENTS.CLICKED_AN_EMAIL,
  WORKFLOW_TRIGGER_EVENTS.CLICKED_AN_IN_APP,
  WORKFLOW_TRIGGER_EVENTS.OPENED_AN_EMAIL,
  WORKFLOW_TRIGGER_EVENTS.OPENED_AN_IN_APP,
  WORKFLOW_TRIGGER_EVENTS.OPENED_PUSH,
] as const;
export const WORKFLOW_EVENTS_TRIGGERED_BY_CHANNEL = [
  WORKFLOW_TRIGGER_EVENTS.SUBSCRIBED_TO_CHANNEL,
  WORKFLOW_TRIGGER_EVENTS.UNSUBSCRIBED_FROM_CHANNEL,
] as const;
export const WORKFLOW_EVENTS_TRIGGERED_BY_FIELD = [WORKFLOW_TRIGGER_EVENTS.FIELD_CHANGED] as const;
export const WORKFLOW_EVENTS_TRIGGERED_BY_JOURNEY = [
  WORKFLOW_TRIGGER_EVENTS.TRIGGERED_BY_WORKFLOW,
] as const;
